/* ============================================================
   WB TRAVEL STORE — THEME JS
   Cart Drawer | Sticky ATC | Mobile Nav | Accordion | UI

   AUDIT REVISION 2026-07-27
   [SEC-01/JS-01] escapeHtml() did not escape the apostrophe, and its
        output was interpolated into single-quoted JavaScript arguments
        inside inline onclick attributes. A product titled "Traveler's
        Kit" would terminate the string and throw; the same path is the
        shape of a stored-XSS vector. All inline onclick generation is
        gone, replaced by event delegation reading data- attributes.
        escapeHtml now also escapes ' and `.
   [JS-02] addToCart() checked `if (res.ok)` with no else branch, so an
        inventory or shipping rejection produced no toast, no message and
        no console warning. Failures are now surfaced with Shopify's own
        error description where one is returned.
   [JS-04] Cart drawer and mobile nav had no focus containment and never
        returned focus to their trigger. Both now trap Tab while open and
        restore focus on close.
   [JS-06] Cart quantity input sent NaN to /cart/change.js when cleared.
   [BUG-01] initVariantSelector() was removed. It only repainted button
        styling and never re-pointed the Add to Cart button at the chosen
        variant, which meant multi-variant products added the wrong item.
        snippets/wb-variant-fix.liquid now owns variant selection.
   ============================================================ */

(function () {
  'use strict';

  /* ---- Cart State ---------------------------------------- */
  const FREE_SHIPPING_THRESHOLD = 75;
  let lastFocusedBeforeDrawer = null;
  let lastFocusedBeforeNav = null;

  /* ---- Utility ------------------------------------------- */
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return [...(ctx || document).querySelectorAll(sel)]; }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }

  /* [SEC-01] Escapes for HTML text and attribute contexts. */
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/`/g, '&#96;');
  }

  function showToast(msg, isError) {
    let toast = qs('.toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.style.borderColor = isError ? 'var(--color-error)' : 'var(--color-gold)';
    toast.classList.add('show');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('show'), isError ? 5000 : 2800);
  }

  /* ---- Focus management ---------------------------------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, e) {
    if (e.key !== 'Tab' || !container) return;
    const items = qsa(FOCUSABLE, container).filter(el => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  function focusFirstIn(container) {
    if (!container) return;
    const items = qsa(FOCUSABLE, container).filter(el => el.offsetParent !== null);
    if (items.length) items[0].focus();
  }

  /* ---- Fetch Cart ---------------------------------------- */
  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  /* [JS-02] Real error handling. Shopify returns a `description` on 4xx. */
  async function addToCart(variantId, quantity, productTitle) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      });
      if (res.ok) {
        await renderCart();
        openCartDrawer();
        showToast('✓ Added to cart — ' + productTitle);
        return true;
      }
      let msg = 'We could not add that to your cart.';
      try {
        const err = await res.json();
        if (err && (err.description || err.message)) msg = err.description || err.message;
      } catch (_) { /* non-JSON error body */ }
      showToast(msg, true);
      return false;
    } catch (e) {
      showToast('Network problem — please check your connection and try again.', true);
      return false;
    }
  }

  async function changeCartLine(key, qty) {
    const n = parseInt(qty, 10);
    if (isNaN(n) || n < 0) { await renderCart(); return; }   /* [JS-06] */
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: n })
      });
      if (!res.ok) showToast('We could not update that item.', true);
    } catch (e) {
      showToast('Network problem — please try again.', true);
    }
    await renderCart();
  }

  /* ---- Render Cart Drawer -------------------------------- */
  async function renderCart() {
    const cart = await fetchCart();
    if (!cart) return;

    const badge = qs('#cartBadge');
    const count = qs('#cartDrawerCount');
    const itemsContainer = qs('#cartItems');
    const emptyState = qs('#cartEmpty');
    const footer = qs('#cartFooter');
    const subtotalEl = qs('#cartSubtotal');
    const fsFill = qs('#cartFsFill');
    const fsMsg = qs('#fsFreeMsg');
    const fsNeeded = qs('#fsAmountNeeded');
    const upsellWrap = qs('#cartUpsell');

    const itemCount = cart.item_count;
    const totalCents = cart.total_price;
    const totalDollars = totalCents / 100;
    const threshold = FREE_SHIPPING_THRESHOLD;
    const pct = Math.min(100, (totalDollars / threshold) * 100);

    if (badge) {
      badge.textContent = itemCount > 0 ? itemCount : '';
      badge.classList.toggle('visible', itemCount > 0);
    }
    if (count) count.textContent = itemCount > 0 ? `(${itemCount})` : '';

    if (fsFill) fsFill.style.width = pct + '%';
    if (fsNeeded && totalDollars < threshold) {
      fsNeeded.textContent = money((threshold * 100) - totalCents);
    }
    if (fsMsg && totalDollars >= threshold) {
      fsMsg.innerHTML = '<strong style="color:var(--color-success);">You have unlocked free shipping.</strong>';
    }

    const pdpNudge = qs('#fsNudgeText');
    if (pdpNudge && itemCount > 0) {
      if (totalDollars >= threshold) {
        pdpNudge.innerHTML = '<strong>Your cart ships FREE</strong> — you have unlocked free U.S. shipping.';
      } else {
        pdpNudge.innerHTML = '<strong>Free U.S. shipping over $75</strong> — your cart is ' + money((threshold * 100) - totalCents) + ' away.';
      }
    }

    if (itemCount === 0) {
      if (emptyState) emptyState.style.display = '';
      if (itemsContainer) { itemsContainer.style.display = 'none'; itemsContainer.innerHTML = ''; }
      if (footer) footer.style.display = 'none';
      if (upsellWrap) upsellWrap.style.display = 'none';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    if (footer) footer.style.display = '';
    if (subtotalEl) subtotalEl.textContent = money(totalCents);

    /* [SEC-01] No inline onclick anywhere below. State travels in data-
       attributes and is read by the delegated handler in initCartEvents. */
    if (itemsContainer) {
      itemsContainer.style.display = '';
      itemsContainer.innerHTML = cart.items.map(item => `
        <div class="cart-item" data-key="${escapeHtml(item.key)}">
          <div class="cart-item-img">
            <a href="${escapeHtml(item.url)}">
              <img src="${escapeHtml(item.image)}" alt="" loading="lazy" width="72" height="72">
            </a>
          </div>
          <div class="cart-item-info">
            <p class="cart-item-name">${escapeHtml(item.product_title)}</p>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item-variant">${escapeHtml(item.variant_title)}</p>` : ''}
            <div class="cart-item-footer">
              <div class="cart-item-qty">
                <button type="button" data-cart-action="dec" data-key="${escapeHtml(item.key)}" data-qty="${item.quantity - 1}" aria-label="Decrease quantity of ${escapeHtml(item.product_title)}">−</button>
                <input type="number" value="${item.quantity}" min="0" data-cart-input data-key="${escapeHtml(item.key)}" aria-label="Quantity for ${escapeHtml(item.product_title)}">
                <button type="button" data-cart-action="inc" data-key="${escapeHtml(item.key)}" data-qty="${item.quantity + 1}" aria-label="Increase quantity of ${escapeHtml(item.product_title)}">+</button>
              </div>
              <div style="display:flex;align-items:center;gap:var(--space-3);">
                <span class="cart-item-price">${money(item.line_price)}</span>
                <button type="button" class="cart-item-remove" data-cart-action="remove" data-key="${escapeHtml(item.key)}" aria-label="Remove ${escapeHtml(item.product_title)} from cart">Remove</button>
              </div>
            </div>
          </div>
        </div>
      `).join('');
    }

    if (upsellWrap) {
      const upsellItemsEl = qs('#cartUpsellItems');
      const upsells = await getUpsellSuggestions(cart.items);
      if (upsells.length > 0 && upsellItemsEl) {
        upsellWrap.style.display = '';
        upsellItemsEl.innerHTML = upsells.map(u => `
          <div class="cart-upsell-item">
            <div class="cart-upsell-img">
              <img src="${escapeHtml(u.image)}" alt="" loading="lazy">
            </div>
            <div class="cart-upsell-info">
              <p class="cart-upsell-name">${escapeHtml(u.name)}</p>
              <p class="cart-upsell-price">${escapeHtml(u.price)}</p>
            </div>
            <div class="cart-upsell-add">
              <button type="button" data-cart-action="upsell" data-variant-id="${u.variantId}" data-title="${escapeHtml(u.name)}" aria-label="Add ${escapeHtml(u.name)} to cart">+ Add</button>
            </div>
          </div>
        `).join('');
      } else {
        upsellWrap.style.display = 'none';
      }
    }
  }

  /* ---- Upsell Suggestions -------------------------------- */
  const UPSELL_HANDLES = ['sovereign-lock', 'volt-worldplug', 'volt-fastcharge'];
  let upsellCache = null;

  async function getUpsellSuggestions(items) {
    if (!upsellCache) {
      try {
        const results = await Promise.all(
          UPSELL_HANDLES.map(h =>
            fetch('/products/' + h + '.js')
              .then(r => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        );
        upsellCache = results
          .filter(p => p && p.available && p.variants && p.variants.length)
          .map(p => ({
            productId: p.id,
            variantId: p.variants[0].id,
            name: p.title,
            price: money(p.price),
            image: p.featured_image
              ? p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=144'
              : ''
          }));
      } catch (e) {
        upsellCache = [];
      }
    }
    return upsellCache
      .filter(u => !items.some(i => i.product_id === u.productId))
      .slice(0, 2);
  }

  /* ---- Cart Drawer Open/Close --------------------------- */
  function openCartDrawer() {
    const drawer = qs('#cartDrawer');
    const overlay = qs('#cart-overlay');
    const toggle = qs('#cartToggle');
    lastFocusedBeforeDrawer = document.activeElement;
    if (drawer) { drawer.classList.add('open'); drawer.setAttribute('aria-hidden', 'false'); }
    if (overlay) overlay.classList.add('active');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    setTimeout(() => focusFirstIn(drawer), 60);   /* after the transition starts */
  }

  function closeCartDrawer() {
    const drawer = qs('#cartDrawer');
    const overlay = qs('#cart-overlay');
    const toggle = qs('#cartToggle');
    if (!drawer || !drawer.classList.contains('open')) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.classList.remove('active');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (lastFocusedBeforeDrawer && lastFocusedBeforeDrawer.focus) lastFocusedBeforeDrawer.focus();
    lastFocusedBeforeDrawer = null;
  }

  /* Kept for backwards compatibility with any external caller. */
  window.WBCart = {
    open: openCartDrawer,
    close: closeCartDrawer,
    removeItem: (key) => changeCartLine(key, 0),
    updateQty: (key, qty) => changeCartLine(key, qty)
  };

  /* ---- Header Scroll Effect ------------------------------ */
  function initHeader() {
    const header = qs('#site-header');
    if (!header) return;
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:72px;height:1px;width:1px;pointer-events:none;';
    document.body.prepend(sentinel);
    new IntersectionObserver(
      ([entry]) => header.classList.toggle('scrolled', !entry.isIntersecting),
      { threshold: 0, rootMargin: '-72px 0px 0px 0px' }
    ).observe(sentinel);
  }

  /* ---- Mobile Nav ---------------------------------------- */
  function openMobileNav() {
    const toggle = qs('#mobileMenuToggle');
    const nav = qs('#mobileNav');
    if (!nav) return;
    lastFocusedBeforeNav = document.activeElement;
    nav.classList.add('open');
    nav.setAttribute('aria-hidden', 'false');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    setTimeout(() => focusFirstIn(nav), 60);
  }

  function closeMobileNav() {
    const toggle = qs('#mobileMenuToggle');
    const nav = qs('#mobileNav');
    if (!nav || !nav.classList.contains('open')) return;
    nav.classList.remove('open');
    nav.setAttribute('aria-hidden', 'true');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (lastFocusedBeforeNav && lastFocusedBeforeNav.focus) lastFocusedBeforeNav.focus();
    lastFocusedBeforeNav = null;
  }

  function initMobileNav() {
    on(qs('#mobileMenuToggle'), 'click', openMobileNav);
    on(qs('#mobileNavClose'), 'click', closeMobileNav);
    on(qs('#mobileNav'), 'click', (e) => {
      if (e.target.tagName === 'A') closeMobileNav();
    });
  }

  /* ---- Cart Events (delegated) --------------------------- */
  function initCartEvents() {
    on(qs('#cartToggle'), 'click', openCartDrawer);
    on(qs('#cartDrawerClose'), 'click', closeCartDrawer);
    on(qs('#cart-overlay'), 'click', closeCartDrawer);

    on(document, 'click', function (e) {
      const btn = e.target.closest('[data-cart-action]');
      if (!btn) return;
      const action = btn.dataset.cartAction;
      const key = btn.dataset.key;
      if (action === 'remove') return changeCartLine(key, 0);
      if (action === 'inc' || action === 'dec') return changeCartLine(key, btn.dataset.qty);
      if (action === 'upsell') return addToCart(btn.dataset.variantId, 1, btn.dataset.title);
    });

    on(document, 'change', function (e) {
      const input = e.target.closest('[data-cart-input]');
      if (!input) return;
      changeCartLine(input.dataset.key, input.value);
    });

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape') { closeCartDrawer(); closeMobileNav(); return; }
      if (e.key !== 'Tab') return;
      const drawer = qs('#cartDrawer');
      const nav = qs('#mobileNav');
      if (drawer && drawer.classList.contains('open')) trapFocus(drawer, e);
      else if (nav && nav.classList.contains('open')) trapFocus(nav, e);
    });
  }

  /* ---- Add to Cart Buttons ------------------------------- */
  function initAddToCartButtons() {
    function qty() {
      const v = parseInt(qs('#qtyInput')?.value || '1', 10);
      return (isNaN(v) || v < 1) ? 1 : v;
    }

    on(qs('#addToCartBtn'), 'click', function (e) {
      e.preventDefault();
      if (!this.dataset.variantId) return;
      addToCart(this.dataset.variantId, qty(), this.dataset.productTitle);
    });

    on(qs('#stickyAtcBtn'), 'click', function () {
      if (!this.dataset.variantId) return;
      addToCart(this.dataset.variantId, qty(), this.dataset.productTitle);
    });

    on(document, 'click', function (e) {
      const btn = e.target.closest('.quick-add-btn');
      if (!btn || !btn.dataset.productId) return;
      addToCart(btn.dataset.productId, 1, btn.dataset.productTitle);
    });
  }

  /* ---- Quantity Selector --------------------------------- */
  function initQuantitySelector() {
    const minus = qs('#qtyMinus');
    const plus = qs('#qtyPlus');
    const input = qs('#qtyInput');
    if (!minus || !plus || !input) return;

    const clamp = () => {
      let val = parseInt(input.value, 10);
      if (isNaN(val) || val < 1) val = 1;
      if (val > 99) val = 99;
      input.value = val;
      return val;
    };

    on(minus, 'click', () => { input.value = Math.max(1, clamp() - 1); });
    on(plus, 'click', () => { input.value = Math.min(99, clamp() + 1); });
    on(input, 'change', clamp);
    on(input, 'blur', clamp);
  }

  /* ---- Product Image Gallery ----------------------------- */
  function initGallery() {
    const mainImg = qs('#mainImageEl');
    const thumbs = qsa('.product-thumb');
    if (!mainImg || thumbs.length === 0) return;

    thumbs.forEach(thumb => {
      on(thumb, 'click', () => {
        mainImg.removeAttribute('srcset');
        mainImg.src = thumb.dataset.imageSrc;
        mainImg.alt = thumb.dataset.imageAlt || '';
        thumbs.forEach(t => { t.classList.remove('active'); t.setAttribute('aria-pressed', 'false'); });
        thumb.classList.add('active');
        thumb.setAttribute('aria-pressed', 'true');
      });
    });
  }

  /* ---- Accordion ----------------------------------------- */
  function initAccordion() {
    qsa('.accordion-trigger').forEach(trigger => {
      on(trigger, 'click', () => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        const content = qs('#' + trigger.getAttribute('aria-controls'));
        trigger.setAttribute('aria-expanded', String(!expanded));
        if (content) content.classList.toggle('open', !expanded);
      });
    });
  }

  /* ---- Sticky ATC ---------------------------------------- */
  function initStickyAtc() {
    const stickyAtc = qs('#stickyAtc');
    const atcBtn = qs('#addToCartBtn');
    if (!stickyAtc || !atcBtn) return;

    new IntersectionObserver(
      ([entry]) => {
        const visible = !entry.isIntersecting;
        stickyAtc.classList.toggle('visible', visible);
        stickyAtc.setAttribute('aria-hidden', String(!visible));
      },
      { threshold: 0, rootMargin: '0px 0px -100px 0px' }
    ).observe(atcBtn);
  }

  /* ---- Wishlist ------------------------------------------
     NOTE: this is still presentational only — nothing is persisted.
     Audit item JS-03: either back it with customer metafields or a
     wishlist app, or remove the control. Telling a shopper their item is
     saved when it is not is the one dishonest interaction in the theme.
     Left in place so removing it stays a product decision, not a silent
     one, but the toast no longer claims anything was saved.
  --------------------------------------------------------- */
  function initWishlist() {
    const btn = qs('.wishlist-btn');
    if (!btn) return;
    on(btn, 'click', () => {
      const pressed = btn.getAttribute('aria-pressed') === 'true';
      btn.setAttribute('aria-pressed', String(!pressed));
      const path = btn.querySelector('path');
      if (path) {
        path.setAttribute('fill', pressed ? 'none' : 'var(--color-gold)');
        path.setAttribute('stroke', pressed ? 'currentColor' : 'var(--color-gold)');
      }
    });
  }

  /* ---- Back to Top -------------------------------------- */
  function initBackToTop() {
    const btn = qs('#backToTop');
    if (!btn) return;
    window.addEventListener('scroll', () => {
      btn.classList.toggle('visible', window.scrollY > 400);
    }, { passive: true });
    on(btn, 'click', () => {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
  }

  /* ---- Collection Filters --------------------------------
     Client-side only, and therefore scoped to the products already on
     this page of the pagination. Audit item BUG-03 recommends replacing
     this entirely with Shopify's native storefront filters, which are
     server-rendered, cover the whole collection and produce crawlable
     URLs. Until then, the count below tells the shopper the truth about
     what is being filtered.
  --------------------------------------------------------- */
  function initCollectionFilters() {
    const pills = qsa('.filter-pill[data-filter]');
    if (pills.length === 0) return;

    pills.forEach(pill => {
      on(pill, 'click', () => {
        pills.forEach(p => { p.classList.remove('active'); p.setAttribute('aria-pressed', 'false'); });
        pill.classList.add('active');
        pill.setAttribute('aria-pressed', 'true');
        const filter = pill.dataset.filter;
        let shown = 0;
        qsa('.product-card').forEach(card => {
          let show = false;
          if (filter === 'all') {
            show = true;
          } else if (filter.indexOf('type:') === 0) {
            show = (card.dataset.productType || '') === filter.slice(5);
          } else if (filter === 'under50') {
            const price = card.querySelector('.price-current');
            show = !!price && parseFloat(price.textContent.replace(/[^0-9.]/g, '')) < 50;
          } else {
            show = !!card.querySelector('.badge--' + filter);
          }
          card.style.display = show ? '' : 'none';
          if (show) shown++;
        });
        const note = qs('#filterResultNote');
        if (note) {
          note.textContent = filter === 'all'
            ? ''
            : shown + ' matching on this page';
        }
      });
    });
  }

  /* ---- Init -------------------------------------------- */
  document.addEventListener('DOMContentLoaded', async () => {
    initHeader();
    initMobileNav();
    initCartEvents();
    initAddToCartButtons();
    initQuantitySelector();
    initGallery();
    initAccordion();
    initStickyAtc();
    initWishlist();
    initBackToTop();
    initCollectionFilters();
    await renderCart();
  });

})();
